<template>
  <div>
    <header-admin :active-section="2" />
    <div>
      <div class="row">
        <div class="col-md-12 mt-5">
          <div class="p-0 text-center">
            <div>
              <h4 class="m-b-5">
                {{ user.fullName }}
              </h4>
              <p class="text-muted">
                {{ user.title }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="col-md-12 text-right">
          <button class="btn btn-grey mr-1" @click="goBack">
            {{ trans("general.back") }}
          </button>
          <button
            class="btn btn-primary saveButton"
            data-test="admin-user-save"
            :disabled="!isValid || loading"
            :title="isValid ? '' : trans('examination.requiredFieldMissing')"
            @click="saveFormData"
          >
            {{ trans("users.saveButton") }}
          </button>
        </div>

        <user-header component-purpose="admin" />

        <div class="tab-content">
          <div id="profile" class="tab-pane active">
            <user-profile
              :errors="errors"
              :user="user"
              :is-new-user="$route.params.id === 'new'"
            />

            <div class="text-right mb-5">
              <button
                v-if="user.initials && !user.blocked"
                data-test="admin-user-send-reset-btn"
                type="button"
                class="btn btn-secondary mr-1"
                :disabled="sendingPasswordReset"
                @click="sendPasswordReset"
              >
                <span v-if="user.is_verified">{{
                  trans("center.sendPasswordReset")
                }}</span>
                <span v-if="!user.is_verified">{{
                  trans("center.resendVerificationEmail")
                }}</span>
              </button>
              <button
                v-if="user.initials"
                class="btn btn-primary"
                :disabled="!isValid || loading"
                :title="
                  isValid ? '' : trans('examination.requiredFieldMissing')
                "
                @click="saveFormData"
              >
                {{ trans("users.saveButton") }}
              </button>
            </div>
          </div>

          <div id="settings" class="tab-pane">
            <user-settings
              :user="user"
              :external-resources="externalResources"
              :ucr="ucr"
              :errors="errors"
              :user-roles="pendingRoles"
              :dropdown-role="dropdownRole(user)"
              @update:user="user = $event"
              @ucr-synced="loadData"
            />

            <!-- User centers -->
            <div class="row mb-5">
              <div class="col-md-12">
                <div class="panel panel-default panel-fill">
                  <div class="panel-heading">
                    <h3 class="panel-title">
                      {{ trans("users.resursHeadline") }}
                    </h3>
                  </div>
                  <div class="panel-body">
                    <div v-if="$route.params.id === 'new'" class="form-group">
                      <div
                        class="form-group primaryCenterSelection"
                        :class="{ 'has-error': errors.primary_center_id }"
                      >
                        <label for="primaryCenter">
                          {{ trans("users.primaryCenterLabel") }}
                        </label>
                        <i class="far fa-asterisk" />
                        <select2
                          id="primaryCenter"
                          v-model="primaryCenterId"
                          name="primaryCenter"
                          disable-clear="true"
                        >
                          <option :value="primaryCenter.id" selected>
                            {{ primaryCenter.name }}
                          </option>
                          <option
                            v-for="(center, index) in centers"
                            :key="index"
                            :value="center.id"
                          >
                            {{ center.text }}
                          </option>
                        </select2>
                        <form-error
                          v-if="errors.primary_center_id"
                          :errors="errors.primary_center_id"
                        />
                      </div>
                    </div>
                    <div v-if="$route.params.id !== 'new'" class="form-group">
                      <label class="tw-block">
                        {{ trans("users.primaryCenterLabel") }}:
                        <span class="tw-font-light">
                          {{ primaryCenter.name }}
                        </span>
                      </label>
                      <button
                        type="button"
                        class="btn btn-primary"
                        data-test="admin-change-primary-centre"
                        @click="showModal"
                      >
                        <i class="fa fa-external-link-alt" />
                        <span>{{
                          trans("users.changePrimaryCenterLabel")
                        }}</span>
                      </button>
                    </div>
                    <connected-centers
                      v-if="$route.params.id !== 'new' && user.id"
                      :user-id="user.id"
                      :scope-centers="scopeCenters"
                      :unrestricted-scope="true"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- User centers -->
          </div>

          <div id="products" class="tab-pane">
            <user-products
              ref="userProducts"
              :user-id="$route.params.id"
              source="admin"
              context=""
              :can-view-clinical-data="canViewClinicalData"
            />
          </div>
        </div>
      </div>
      <VueJsDialogMixinWrapper
        v-if="showMoveUserDialog"
        :data-component="moveUserDialogComponent"
        :data-options="moveUserDialogOptions"
        @user-settings-changes="loadData"
        @close="closeModal"
      />
    </div>
  </div>
</template>

<script>
import ConnectedCenters from "../../../User/ConnectedCenters.vue";
import HeaderAdmin from "../../Header.vue";
import UserHeader from "../../../User/UserHeader.vue";
import UserProfile from "./UserProfile.vue";
import UserProducts from "./UserProducts.vue";
import UserSettings from "./UserSettings.vue";

import UserRolesMixin from "../../../../mixins/UserRolesMixin.vue";
import Notifications from "../../../Utils/notifications";
import VueJsDialogMixinWrapper from "../../../../mixins/VueJsDialogMixinWrapper.vue";
import AdminMoveUser from "./MoveUser.vue";
import { markRaw } from "vue";
import { practitionerHttpService } from "../../../../app/practitioner-portal/store/practitioner-http-service";
import { hasMissingRequiredPlatformAttributes } from "../../../PlatformAttributes/platformAttributeValidation";
export default {
  components: {
    ConnectedCenters,
    UserProfile,
    UserProducts,
    UserSettings,
    UserHeader,
    HeaderAdmin,
    VueJsDialogMixinWrapper,
  },
  mixins: [UserRolesMixin],
  beforeRouteEnter(to, from, next) {
    next((instance) => {
      instance.backRoute = from.path;
    });
  },
  data() {
    return {
      loading: false,
      sendingPasswordReset: false,
      user: {},
      primaryCenter: {
        id: null,
      },
      primaryCenterId: "",
      centers: "",
      externalResources: "",
      ucr: {
        active: true,
        id: "",
      },
      products: {
        notifications_new_examinations: false,
        consensus_notifications: false,
        new_consensus_channel_notifications: false,
        notification_new_document_consult_answer: false,
        unanswered_cases: false,
        cases_without_photos: false,
      },
      errors: {},
      roles: [],
      pendingRoles: {
        consultant: false,
        senior_consultant: false,
        wound_user: false,
        user: false,
        user_without_clinical_view: false,
        center_admin: false,
        region_admin: false,
        external_resource: false,
        region_external_resource: false,
        admin: false,
        case_admin: false,
        ai_tester: false,
        consultation_creator: false,
        consultation_photographer: false,
        study_admin: false,
        study_participator: false,
      },
      originalBlocked: false,
      backRoute: "/",
      showMoveUserDialog: false,
      moveUserDialogComponent: markRaw(AdminMoveUser),
    };
  },
  computed: {
    canViewClinicalData() {
      return !this.pendingRoles.user_without_clinical_view;
    },
    scopeCenters() {
      // `centers` arrives as an object keyed by index plus a stray scalar
      // (`created_at`), so iterate values and keep only the centre rows.
      return Object.values(this.centers ?? {})
        .filter((center) => center && typeof center === "object" && center.id)
        .map((center) => ({
          id: center.id,
          name: center.text,
        }));
    },
    moveUserDialogOptions() {
      return {
        userId: this.user.id,
        primaryCenter: this.primaryCenter,
        userRoles: this.pendingRoles,
      };
    },
    isValid: function () {
      const isNew = this.$route.params.id === "new";
      if (
        !this.user.culture ||
        (!isNew && !this.user.username) ||
        !this.user.email ||
        !this.user.firstname ||
        !this.user.lastname ||
        !this.primaryCenterId
      ) {
        return false;
      }
      if (this.hasMissingRequiredPlatformAttributes) {
        return false;
      }
      if (isNew && !this.hasMainRole) {
        return false;
      }
      return true;
    },
    hasMainRole: function () {
      return (
        this.pendingRoles.user ||
        this.pendingRoles.user_without_clinical_view ||
        this.pendingRoles.consultant ||
        this.pendingRoles.senior_consultant ||
        this.pendingRoles.wound_user ||
        this.pendingRoles.consultation_photographer
      );
    },
    hasMissingRequiredPlatformAttributes: function () {
      return hasMissingRequiredPlatformAttributes(
        this.user.platform_attributes,
        this.user.platform_attribute_values,
      );
    },
  },
  created: function () {
    this.loadRoles();
    this.loadData();
  },
  methods: {
    showModal() {
      this.showMoveUserDialog = true;
    },
    closeModal() {
      this.showMoveUserDialog = false;
    },
    goBack: function () {
      this.$router.go(-1);
    },
    loadRoles: function () {
      practitionerHttpService
        .get("/api/internal/admin/roles")
        .then((data) => {
          let collection = data.data;
          this.roles = collection;
        })
        .catch((error) => {
          console.log("Loading roles error: ", error);
        });
    },
    mapRolesToBoolean(roles) {
      return (roles ?? []).reduce((acc, role) => {
        acc[role.name] = true;
        return acc;
      }, {});
    },
    loadData: function () {
      this.loading = true;
      practitionerHttpService
        .get("/api/internal/admin/users/" + this.$route.params.id)
        .then((data) => {
          let collection = data.data;
          this.user = collection.user;
          this.originalBlocked = !!collection.user.blocked;
          this.primaryCenter = collection.primaryCenter;
          this.primaryCenterId = collection.primaryCenter.id;
          this.centers = collection.centers;
          this.externalResources = collection.externalResources;
          this.ucr = collection.ucr;

          // Declare dynamically updating variable and assign role to true if the role is in the assigned user roles list
          this.pendingRoles = this.mapRolesToBoolean(this.user.roles);

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.userErrorMessage(error);
        });
    },
    saveFormData: function () {
      // Show a confirmation dialog when the user is being deactivated (pendingRoles.user transitions to false).

      let currentRoles = this.mapRolesToBoolean(this.user.roles);
      let noPendingExternalResourceRole =
        !this.pendingRoles.external_resource &&
        !this.pendingRoles.region_external_resource;

      let message = [
        this.user.blocked && !this.originalBlocked
          ? this.trans("general.modalBlockUserMessage")
          : false,
        noPendingExternalResourceRole && currentRoles.external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalExternalResourceRemoveUserMessage",
            )
          : false,
        noPendingExternalResourceRole && currentRoles.region_external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalRegionExternalResourceRemoveMessage",
            )
          : false,
      ].filter(Boolean);

      if (message.length > 0) {
        this.$vueAlert
          .confirm(undefined, undefined, undefined, {
            html: message.map((msg) => {
              return msg;
            })[0],
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
          })
          .then(() => {
            // If confirmed, proceed with saving.
            this.submitForm();
          })
          .catch(() => {
            // If canceled, abort the save.
            return false;
          });
      } else {
        this.submitForm();
      }
    },
    submitForm() {
      this.user.primary_center_id = this.primaryCenterId;
      this.user.products = this.products;
      this.user.userRoles = this.pendingRoles;
      this.user.settings_products = this.$refs.userProducts.getProducts();

      this.errors = {};
      this.loading = true;

      if (this.$route.params.id === "new") {
        // don't create users that can't create cases, unless they are only supposed to be photographers
        if (this.user.userRoles.consultation_photographer === false) {
          this.user.userRoles.consultation_creator = true;
        }
      }

      practitionerHttpService
        .post("/api/internal/admin/users/" + this.user.id, this.user)
        .then((response) => {
          Notifications.saveSuccess();

          if (response.data.isNew) {
            this.$router.push({ name: "AdminUsers" });
          }

          this.loadData();
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.errors = error.response?.data ?? {};
          this.userErrorMessage(error);
        });
    },
    sendPasswordReset: function () {
      if (this.sendingPasswordReset) {
        return;
      }
      this.sendingPasswordReset = true;
      practitionerHttpService
        .post("/api/internal/admin/users/password/reset/" + this.user.id)
        .then((response) => {
          this.sendingPasswordReset = false;
          if (response.data?.success !== 1) {
            Notifications.warn();
            return;
          }
          Notifications.saveSuccessCustomText(
            "general.notificationPasswordResetSent",
          );
        })
        .catch((error) => {
          this.sendingPasswordReset = false;
          this.userErrorMessage(error);
        });
    },
    getExternalResourceModalTranslation(translationKey) {
      return this.trans(translationKey, {
        userGlue: this.trans("general.modalExternalResourceRemoveUserGlue"),
        theirGlue: this.trans("general.modalExternalResourceRemoveTheirGlue"),
      });
    },
  },
};
</script>
